import { X } from 'lucide-react'
import type { FormEventHandler, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Drawer, type DrawerSize } from '@/components/ui/drawer'
import { cn } from '@/lib/utils'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  size?: Exclude<DrawerSize, 'nav'>
  footer?: ReactNode
  onSubmit?: FormEventHandler<HTMLFormElement>
  dismissible?: boolean
  bodyClassName?: string
  children: ReactNode
}

export function FormDrawer({
  open,
  onOpenChange,
  title,
  description,
  icon,
  size = 'md',
  footer,
  onSubmit,
  dismissible = true,
  bodyClassName,
  children,
}: Props) {
  const { t } = useTranslation()
  const body = (
    <>
      <header className="flex shrink-0 items-start gap-3 border-b px-5 py-4">
        {icon ? <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue [&_svg]:size-4">{icon}</span> : null}
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8 shrink-0"
          aria-label={t('common.close')}
          disabled={!dismissible}
          onClick={() => onOpenChange(false)}
        >
          <X />
        </Button>
      </header>
      <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 py-4', bodyClassName)}>{children}</div>
      {footer ? <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-muted/30 px-5 py-3">{footer}</footer> : null}
    </>
  )

  return (
    <Drawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !dismissible) return
        onOpenChange(v)
      }}
      title={typeof title === 'string' ? title : t('common.close')}
      side="right"
      size={size}
    >
      {onSubmit ? (
        <form className="flex h-full flex-col" onSubmit={onSubmit}>
          {body}
        </form>
      ) : (
        <div className="flex h-full flex-col">{body}</div>
      )}
    </Drawer>
  )
}
